import useMediaQuery from "@mui/material/useMediaQuery";
import { useContext, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { Controller, useForm } from "react-hook-form";
import { BsGlobe, BsArrowLeft } from "react-icons/bs";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../../auth/AuthProvider";
import OtpInput from "../../../components/reusables/OtpInput";
import { ButtonCustom } from "../../../components/sharedComponents/ButtonCustom";
import { FirstHeader } from "../../../components/sharedComponents/FirstHeader";
import { CallAPI } from "../../../config/api/ApiConfig";
import { LocaleContext } from "../../../config/localization/LanguageProvider";
import { iconPath } from "../../../constants/constants";
import { useTheme } from "../../../context/ThemeContext";
import { PATH_PAGE } from "../../../routes/Paths";
import { MobileLogin } from "./MobileLogin";
import { IoMailOutline } from "react-icons/io5";
import { FiPhone } from "react-icons/fi";
import { FaQuestionCircle } from "react-icons/fa";

export default function Login() {
  const navigate = useNavigate();
  const path = iconPath();
  const logo = `${path}logo.png`;
  const { locale, setLocale } = useContext(LocaleContext);
  const { setUser } = useContext(AuthContext);
  const { messages } = useIntl();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const id = searchParams.get("id");
  const slug = searchParams.get("slug");
  const token = searchParams.get("token");

  const [checkbox, setCheckbox] = useState("email");
  const [phoneNum, setPhoneNumber] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [mobileSubmitted, setMobileSubmitted] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const isTablet = useMediaQuery("(max-width:1024px)");
  const classFlex = "justify-content-center align-items-center d-flex";

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm({
    mode: "all",
    defaultValues: {
      email: "",
      phone: "",
      otp: "",
    },
  });

  const { refetch: sendLoginOtp } = CallAPI({
    name: "sendLoginOtp",
    url: "/mentor/v1/auth/send-login-otp",
    refetchOnWindowFocus: false,
    method: "post",
    body: {
      [checkbox === "email" ? "email" : "mobile"]:
        checkbox === "email"
          ? watch("email")
          : watch("phone")?.split(" ").slice(1).join(""),
      ...(checkbox !== "email" && {
        country_code: watch("phone")?.split(" ")[0].replace("+", ""),
      }),
      slug: searchParams.get("slug") || "",
    },
    enabled: false,
    onSuccess: (res) => {
      if (checkbox === "email") {
        setEmailSubmitted(true);
        setMobileSubmitted(false);
      } else if (checkbox === "mobile") {
        setMobileSubmitted(true);
        setEmailSubmitted(false);
      }
    },
    onError: (err) => {
      toast.error(err.data.message);
    },
  });

  const { refetch: mentorRedirectLogin } = CallAPI({
    name: "mentorRedirectLogin",
    url: `/get_user_data/${id}/${slug}`,
    method: "get",
    enabled: false,
    headers: id && slug && token ? { Authorization: `Bearer ${token}` } : {},
    onSuccess: (res) => {
      setUser(res?.data);
      localStorage.setItem("user_data", JSON.stringify(res?.data));
      const themeParam = searchParams.get("slug");
      if (themeParam) {
        localStorage.setItem("theme_slug", themeParam);
      }
      localStorage.setItem(
        "branding",
        JSON.stringify(res?.data?.branding || null),
      );
      navigate(PATH_PAGE.dashboard);
      toast.success(messages["login.success"]);
    },
    onError: (err) => {
      toast.error(err.data.message);
    },
  });

  const { refetch: verifyLoginOtp } = CallAPI({
    name: "verifyLoginOtp",
    url: "/mentor/v1/auth/verify-login-otp",
    refetchOnWindowFocus: false,
    method: "post",
    body: {
      [checkbox === "email" ? "email" : "mobile"]:
        checkbox === "email"
          ? watch("email")
          : watch("phone")?.split(" ").slice(1).join(""),
      otp: watch("otp"),
      ...(checkbox !== "email" && {
        country_code: watch("phone")?.split(" ")[0].replace("+", ""),
      }),
      slug: searchParams.get("slug") || "",
    },
    enabled: false,
    onSuccess: (res) => {
      setUser(res?.data);
      localStorage.setItem("user_data", JSON.stringify(res?.data));
      const themeParam = searchParams.get("slug");
      if (themeParam) {
        localStorage.setItem("theme_slug", themeParam);
      }
      localStorage.setItem(
        "branding",
        JSON.stringify(res?.data?.branding || null),
      );
      navigate(PATH_PAGE.dashboard);
      toast.success(messages["login.success"]);
    },
    onError: (err) => {
      toast.error(err.data.message);
    },
  });

  const onSubmit = async (data) => {
    setDisabled(true);
    if (!emailSubmitted && !mobileSubmitted) {
      await sendLoginOtp();
    } else {
      await verifyLoginOtp();
    }
    setDisabled(false);
  };

  const { themeData } = useTheme();

  const { refetch: callLoginSocial } = CallAPI({
    name: "loginSocial",
    url: `/mentor/v1/auth/user/${id}/${token}`,
    refetchOnWindowFocus: false,
    enabled: false,
    method: "post",
    onSuccess: (res) => {
      localStorage.setItem("user_data", JSON.stringify(res?.data));
      setUser(res?.data);
      navigate("/dashboard");
      toast.success("login successfully");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
  });

  const { refetch: loginLinkedin } = CallAPI({
    name: "linkedin",
    url: "/mentor/linkedin-login",
    refetchOnWindowFocus: false,
    enabled: false,
    baseURL: process.env.REACT_APP_DOMAIN_SECOND,
    onSuccess: (res) => {
      if (res?.data?.data) {
        window.location.href = res?.data?.data;
      }
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
  });

  const { refetch: loginGoogle } = CallAPI({
    name: "google",
    url: "/mentor/google-login",
    refetchOnWindowFocus: false,
    enabled: false,
    baseURL: process.env.REACT_APP_DOMAIN_SECOND,
    onSuccess: (res) => {
      if (res?.data?.data) {
        window.location.href = res?.data?.data;
      }
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
  });

  useEffect(() => {
    if (id && slug && token) {
      void mentorRedirectLogin();
    }
  }, [id, slug, token, mentorRedirectLogin]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="auth-page d-flex justify-content-center align-items-center"
    >
      <Container className="pt-5">
        {/* Logo */}
        <Row className={`${classFlex} mb-2 text-center`}>
          <Col xs={12} sm={6}>
            {themeData?.logo_url ? (
              <div className="login-company-logo-wrapper">
                <img
                  src={themeData?.logo_url}
                  alt="logo"
                  className="company-logo w-[160px] rounded-xl mx-auto block shadow-sm"
                />
                <p className="text-center text-2xl font-semibold capitalize m-0 mt-2">
                  {themeData?.company_name}
                </p>
              </div>
            ) : (
              <div className="login-company-logo-wrapper">
                <img
                  src={logo}
                  alt="logo"
                  className="logo_size"
                  // className="company-logo w-[160px] rounded-xl mx-auto block shadow-sm"
                />
              </div>
            )}
          </Col>
        </Row>

        {/* Auth Card */}
        <Row
          className={`auth-card w-${isTablet ? 100 : 50} mx-auto mt-4`}
          style={{ maxWidth: "500px" }}
        >
          <Col>
            {emailSubmitted || mobileSubmitted ? (
              <>
                {/* Back to login */}
                <button
                  type="button"
                  className="auth-link d-flex align-items-center gap-2 mb-4"
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    fontSize: "0.9rem",
                  }}
                  onClick={() => {
                    setEmailSubmitted(false);
                    setMobileSubmitted(false);
                  }}
                >
                  <BsArrowLeft size={16} />
                  {messages["back_to_login"] || "Back to Login"}
                </button>

                {/* Heading */}
                <h2 className="auth-heading text-center mb-3">
                  {messages["enter_verification_code"] ||
                    "Enter Verification Code"}
                </h2>

                {/* Subtitle */}
                <p className="auth-text-gray text-center mb-1">
                  {messages["sent_code_to"] || "We've sent a 4-digit code to"}
                </p>
                <p
                  className="text-center mb-4"
                  style={{
                    color: "var(--accent)",
                    fontWeight: 600,
                  }}
                >
                  {checkbox === "email" ? watch("email") : watch("phone")}
                </p>

                {/* OTP inputs */}
                <Row className="mb-4">
                  <Col className="position-relative">
                    <Controller
                      name="otp"
                      control={control}
                      rules={{
                        required: messages["mobile_required"],
                        minLength: {
                          value: 4,
                          message: messages["otp_min"],
                        },
                        maxLength: {
                          value: 4,
                          message: messages["otp_max"],
                        },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <OtpInput
                          length={4}
                          type="text"
                          onChange={onChange}
                          value={value}
                        />
                      )}
                    />
                    {errors.otp && (
                      <span className="mentor-input__field-error text-center w-100 ">
                        {errors.otp?.message}
                      </span>
                    )}
                  </Col>
                </Row>

                {/* Resend */}
                <Row className="my-4 py-2">
                  <Col className="text-center">
                    <button
                      type="button"
                      className="resend-otp"
                      onClick={sendLoginOtp}
                    >
                      {messages["didnt_receive"]} {messages["resend_otp"]}
                    </button>
                  </Col>
                </Row>

                {/* Verify button */}
                <Row className="mb-3">
                  <Col>
                    <ButtonCustom
                      className="w-100 auth-cta"
                      text="verify_continue"
                      type="submit"
                      disabled={disabled}
                    />
                  </Col>
                </Row>

                {/* Secure divider */}
                <div className="auth-divider my-4 py-2">
                  <span>{messages["secure"] || "SECURE"}</span>
                </div>

                {/* Timer text */}
                <p
                  className="auth-text-gray text-center mb-0"
                  style={{ fontSize: "0.8rem" }}
                >
                  {messages["code_valid_time"] ||
                    "This code is valid for 10 minutes. Never share this code with anyone for security reasons."}
                </p>
              </>
            ) : (
              <>
                {/* Language selector */}
                <Row className="mb-4">
                  <Col className="d-flex justify-content-start">
                    <div className="language-selection">
                      <BsGlobe />
                      <Form.Select
                        onChange={(e) => setLocale(e.target.value)}
                        value={locale}
                      >
                        <option value="en">{messages["english"]}</option>
                        <option value="ar">{messages["arabic"]}</option>
                      </Form.Select>
                    </div>
                  </Col>

                  {/* Heading */}
                  <Col xs={12} className={classFlex}>
                    <FirstHeader
                      text="sign in"
                      className="FirstHeaderDefault auth-heading my-4"
                    />
                  </Col>

                  {/* Method tabs */}
                  <Col xs={12}>
                    <div className="auth-method-tabs">
                      <label
                        className={`auth-method-tab ${checkbox === "email" ? "active" : ""}`}
                      >
                        <input
                          type="radio"
                          name="loginMethod"
                          value="email"
                          checked={checkbox === "email"}
                          onChange={(e) => setCheckbox(e.target.value)}
                        />
                        <IoMailOutline size={18} />
                        {messages["login.via.email"]}
                      </label>
                      <label
                        className={`auth-method-tab ${checkbox === "mobile" ? "active" : ""}`}
                      >
                        <input
                          type="radio"
                          name="loginMethod"
                          value="mobile"
                          checked={checkbox === "mobile"}
                          onChange={(e) => setCheckbox(e.target.value)}
                        />
                        <FiPhone size={18} />
                        {messages["login.via.mobile"]}
                      </label>
                    </div>
                  </Col>
                </Row>

                {/* Email / Mobile input */}
                <Row className="mb-4">
                  <Col>
                    {checkbox === "mobile" ? (
                      <MobileLogin
                        phoneNum={phoneNum}
                        setPhoneNumber={setPhoneNumber}
                        control={control}
                        placeholder={messages["enter.mobile"]}
                      />
                    ) : (
                      <div className="mentor-input">
                        <label className="mentor-input__label auth-label">
                          {messages["e_mail"]}
                        </label>
                        <input
                          className="auth-input"
                          type="text"
                          name="email"
                          placeholder={messages["enter.email"]}
                          {...register("email", {
                            required: messages["email_required"],
                            pattern: {
                              value:
                                /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                              message: messages["email_invalid"],
                            },
                          })}
                        />
                        {errors.email && (
                          <span className="mentor-input__field-error">
                            {errors.email?.message}
                          </span>
                        )}
                      </div>
                    )}
                  </Col>
                </Row>

                {/* Remember me */}
                <Row className="mb-4">
                  <Col md={6}>
                    <Form.Check
                      className="position-relative"
                      type="checkbox"
                      id="default-checkbox"
                      label={messages["Remember_Me"]}
                    />
                  </Col>
                </Row>

                {/* CTA */}
                <Row className="mb-4">
                  <Col>
                    <ButtonCustom
                      className="w-100 auth-cta"
                      text="send_OTP"
                      type="submit"
                      disabled={disabled}
                    />
                  </Col>
                </Row>

                {/* Divider */}
                <div className="auth-divider my-4">
                  <span>{messages["common.or"]}</span>
                </div>

                {/* Help link */}
                <Row className="mb-2">
                  <Col className="text-center">
                    <a
                      href="#help"
                      className="auth-link d-inline-flex align-items-center gap-1"
                      style={{ fontSize: "0.9rem" }}
                    >
                      <FaQuestionCircle size={12} />
                      {messages["how_can_we_help"] || "Hi! How can we help?"}
                    </a>
                  </Col>
                </Row>
              </>
            )}
          </Col>
        </Row>
      </Container>
    </form>
  );
}
